/**
 * Queries and the one mutation of the new starter flow (`/starters/*`, 2 Oct).
 *
 * Every case is assembled from Ivanti on the server, several reads deep, so
 * the list is kept for half a minute rather than refetched on every focus.
 * Confirming an account rewrites the case, so it refreshes both the case and
 * the list, and `/auth/me` (whose `has_starter_cases` drives the nav entry).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  confirmStarterAccount as sdkConfirmStarterAccount,
  getStarterCase as sdkGetStarterCase,
  getStarterPrefill as sdkGetStarterPrefill,
  listStarterCases as sdkListStarterCases,
  type StarterCase,
  type StarterCaseListResponse,
  type StarterPrefillResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import type { StarterStep } from "./lib/starters";

export const starterKeys = {
  all: ["starters"] as const,
  list: () => [...starterKeys.all, "list"] as const,
  detail: (caseId: string) => [...starterKeys.all, "detail", caseId] as const,
  prefill: (caseId: string, step: StarterStep) =>
    [...starterKeys.all, "prefill", caseId, step] as const,
};

/** The cases the signed-in person may see. `enabled` lets a badge skip the read. */
export function useStarterCases({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: starterKeys.list(),
    queryFn: async (): Promise<StarterCaseListResponse> => {
      const { data } = await sdkListStarterCases({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 30_000,
  });
}

/** One case, read fresh by the server. */
export function useStarterCase(caseId: string) {
  return useQuery({
    queryKey: starterKeys.detail(caseId),
    queryFn: async (): Promise<StarterCase> => {
      const { data } = await sdkGetStarterCase({
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 15_000,
  });
}

/** The offering and first answers of a step's form; null keeps it switched off. */
export function useStarterPrefill(target: { caseId: string; step: StarterStep } | null) {
  return useQuery({
    queryKey:
      target === null
        ? [...starterKeys.all, "prefill", "none"]
        : starterKeys.prefill(target.caseId, target.step),
    queryFn: async (): Promise<StarterPrefillResponse> => {
      if (target === null) {
        throw new Error("No starter step");
      }
      logger.debug("[starters] prefill", target.caseId, target.step);
      const { data } = await sdkGetStarterPrefill({
        path: { case_id: target.caseId, step: target.step },
        throwOnError: true,
      });
      return data;
    },
    enabled: target !== null,
    // A form opened from the case starts from what the case says now.
    staleTime: 0,
    retry: false,
  });
}

/** Confirm which directory person is the starter: a candidate, or anyone picked. */
export function useConfirmStarterAccount(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      employeeId,
      fromDirectory = false,
    }: {
      employeeId: string;
      /** Picked from the directory search, not a candidate (5 Oct). */
      fromDirectory?: boolean;
    }): Promise<StarterCase> => {
      logger.debug("[starters] confirm account", caseId, employeeId, fromDirectory);
      const { data } = await sdkConfirmStarterAccount({
        path: { case_id: caseId },
        body: { employee_id: employeeId, from_directory: fromDirectory },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(starterKeys.detail(caseId), updated);
      void queryClient.invalidateQueries({ queryKey: starterKeys.list() });
      // The access request page shows the same gate and its items wait on it.
      void queryClient.invalidateQueries({ queryKey: ["access"] });
    },
  });
}
